import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, ChevronRight, Building2, FlaskConical, BookOpen,
  Wifi, Dumbbell, Coffee, Heart, Monitor, Loader2, ArrowUpRight, LayoutGrid
} from 'lucide-react';
import { fetchSaranaPrasarana } from '../utils/api';

// Peta nama ikon (dari admin) → komponen lucide, fallback Building2
const ICON_MAP = { Building2, FlaskConical, BookOpen, Wifi, Dumbbell, Coffee, Heart, Monitor };
const resolveIcon = (name) => {
  const Ic = ICON_MAP[name] || Building2;
  return <Ic size={20} />;
};

const imageVariant = (src, width) => {
  if (!supportsResponsiveImage(src)) return src;
  return `${src}${src.includes('?') ? '&' : '?'}w=${width}`;
};

const supportsResponsiveImage = (src) => src?.includes('/api/public/media/')
  || /\/facilities\/[a-f0-9-]{36}\.webp(?:\?|$)/i.test(src);

const toSlug = (value = '') => value
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

// Transform respons API → bentuk yang dipakai komponen ini
const mapCategories = (cats) =>
  (cats || []).map((c) => ({
    id: c.slug,
    label: c.label,
    icon: resolveIcon(c.icon),
    items: (c.items || []).map((it) => ({
      slug: it.slug,
      name: it.name,
      desc: it.deskripsi,
      image: it.imageUrl,
      details: it.details || [],
    })),
  }));

const FACILITIES = [
  {
    id: 'akademik',
    label: 'Fasilitas Akademik',
    icon: <Building2 size={20} />,
    items: [
      {
        name: 'Ruang Kuliah Modern',
        desc: 'Dilengkapi dengan LCD proyektor, AC, whiteboard interaktif, dan kursi ergonomis untuk mendukung proses pembelajaran yang nyaman.',
        image: 'https://images.pexels.com/photos/8197557/pexels-photo-8197557.jpeg?w=600',
        details: ['20 ruang kelas berkapasitas 30–50 mahasiswa', 'Proyektor dan sound system setiap ruangan', 'AC dan pencahayaan optimal', 'Koneksi WiFi kampus'],
      },
      {
        name: 'Laboratorium Keperawatan',
        desc: 'Lab keperawatan modern dengan manikin anatomi, phantom keperawatan dasar, dan peralatan medis simulasi untuk praktik klinik mahasiswa.',
        image: 'https://images.pexels.com/photos/35645528/pexels-photo-35645528.jpeg?w=600',
        details: ['Manikin simulasi CPR & basic life support', 'Peralatan nursing procedure lengkap', 'Ruang OSCE (Ujian Praktik Klinik)', 'Kapasitas 30 mahasiswa/sesi'],
      },
      {
        name: 'Laboratorium Farmasi & Kimia',
        desc: 'Lab farmasi dilengkapi dengan alat analitik, spektrofotometer, HPLC, dan perlengkapan formulasi sediaan farmasi untuk praktikum mahasiswa farmasi.',
        image: 'https://images.unsplash.com/photo-1766297248027-611186dbccc1?w=600&q=80',
        details: ['Spektrofotometer UV-Vis', 'HPLC (High Performance Liquid Chromatography)', 'Alat formulasi solid, liquid, steril', 'Lemari asam & biosafety cabinet'],
      },
      {
        name: 'Laboratorium Komputer & IT',
        desc: 'Lab komputer dengan 60 unit PC terbaru, koneksi internet berkecepatan tinggi, dan software kesehatan untuk mendukung pembelajaran berbasis digital.',
        image: 'https://images.unsplash.com/photo-1766297247924-6638d54e7c89?w=600&q=80',
        details: ['60 unit komputer spek tinggi', 'Internet fiber optic 1 Gbps', 'Software SPSS, ATLAS.ti, AutoCAD', 'Ruang e-learning dan webinar'],
      },
    ],
  },
  {
    id: 'penunjang',
    label: 'Fasilitas Penunjang',
    icon: <Building2 size={20} />,
    items: [
      {
        name: 'Perpustakaan Terpadu',
        desc: 'Perpustakaan dengan koleksi lebih dari 12.000 judul buku teks, jurnal ilmiah, dan akses database kesehatan nasional dan internasional.',
        image: 'https://images.unsplash.com/photo-1675583152779-9fe454abe3a4?w=600&q=80',
        details: ['12.000+ judul koleksi buku kesehatan', 'Akses e-journal ProQuest, EBSCO, Garuda', 'Ruang baca individu dan diskusi kelompok', 'Sistem RFID dan katalog online (OPAC)'],
      },
      {
        name: 'Aula Serbaguna',
        desc: 'Aula representatif berkapasitas 500 orang untuk kegiatan wisuda, seminar nasional, kuliah umum, dan kegiatan kemahasiswaan.',
        image: 'https://customer-assets.emergentagent.com/job_jambi-health-edu/artifacts/xk7yu4he_gedung%204.webp',
        details: ['Kapasitas 500 undangan/tamu', 'Sistem sound dan lighting profesional', 'Tersedia untuk seminar dan wisuda', 'Area parkir luas'],
      },
      {
        name: 'Masjid & Mushola Kampus',
        desc: 'Fasilitas ibadah yang representatif dan nyaman bagi seluruh sivitas akademika STIKES HI Jambi.',
        image: 'https://images.unsplash.com/photo-1724300394152-4da702e0706a?w=600&q=80',
        details: ['Masjid utama berkapasitas 200 jamaah', 'Mushola di setiap gedung', 'Tempat wudhu terpisah putra-putri', 'Agenda kajian rutin mingguan'],
      },
      {
        name: 'Kantin & Area Kuliner',
        desc: 'Kantin kampus bersih dan nyaman dengan pilihan menu makanan sehat yang terjangkau untuk mahasiswa dan civitas akademika.',
        image: 'https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?w=600',
        details: ['Menu makanan bervariasi & bergizi', 'Area makan indoor dan outdoor', 'Kantin sehat dengan pilihan menu halal', 'Koperasi mahasiswa'],
      },
    ],
  },
  {
    id: 'olahraga',
    label: 'Fasilitas Olahraga',
    icon: <Dumbbell size={20} />,
    items: [
      {
        name: 'Lapangan Olahraga Multi-Fungsi',
        desc: 'Lapangan outdoor multi-fungsi untuk kegiatan olahraga mahasiswa, termasuk basket, voli, dan badminton.',
        image: 'https://images.pexels.com/photos/2968077/pexels-photo-2968077.jpeg?w=600',
        details: ['Lapangan basket standar FIBA', 'Lapangan voli outdoor', 'Lapangan badminton indoor', 'Tersedia untuk UKM olahraga'],
      },
      {
        name: 'Ruang Fitness & Kebugaran',
        desc: 'Ruang fitness dengan peralatan olahraga modern untuk menjaga kebugaran mahasiswa dan karyawan STIKES HI Jambi.',
        image: 'https://images.unsplash.com/photo-1598802213955-5610439dd5cc?w=600&q=80',
        details: ['Peralatan cardio (treadmill, sepeda statis)', 'Peralatan beban dan resistance training', 'Jam operasional 06.00–21.00', 'Didampingi instruktur bersertifikat'],
      },
    ],
  },
];

const FacilityCard = ({ item }) => (
  <Link
    to={`/sarana-prasarana/${item.slug || toSlug(item.name)}`}
    className="flex h-full flex-col bg-white dark:bg-[#1A182E] rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group focus:outline-none focus:ring-2 focus:ring-[#7B2FBE] focus:ring-offset-2 dark:focus:ring-offset-[#121020]"
    aria-label={`Lihat detail ${item.name}`}
  >
    <div className="relative h-48 overflow-hidden">
      {item.image ? (
        <img
          src={imageVariant(item.image, 800)}
          srcSet={supportsResponsiveImage(item.image)
            ? `${imageVariant(item.image, 480)} 480w, ${imageVariant(item.image, 800)} 800w, ${imageVariant(item.image, 1200)} 1200w`
            : undefined}
          sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
          alt={item.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#552080] to-[#1A182E] text-white/70">
          <Building2 size={36} aria-label="Foto fasilitas belum tersedia" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      <h3 className="absolute bottom-3 left-4 right-4 text-white font-bold font-heading text-sm leading-tight">{item.name}</h3>
    </div>
    <div className="flex flex-1 flex-col p-5">
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{item.desc}</p>
      <ul className="space-y-1.5 flex-1">
        {item.details.map((d, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7B2FBE] flex-shrink-0 mt-1.5" />
            {d}
          </li>
        ))}
      </ul>
      <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4 text-sm font-semibold text-[#7B2FBE] dark:border-gray-800 dark:text-purple-300">
        Lihat detail
        <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </div>
    </div>
  </Link>
);

const SaranaPrasarana = () => {
  const { categorySlug } = useParams();
  const [categories, setCategories] = useState(null); // null = sedang memuat

  useEffect(() => {
    let alive = true;
    fetchSaranaPrasarana()
      .then((cats) => {
        if (!alive) return;
        const mapped = mapCategories(cats);
        setCategories(mapped);
      })
      .catch(() => {
        if (!alive) return;
        setCategories(FACILITIES); // fallback ke data statis bila API tidak tersedia
      });
    return () => {
      alive = false;
    };
  }, []);

  const list = categories || [];
  const loading = categories === null;
  const current = categorySlug ? list.find((category) => category.id === categorySlug) : null;
  const categoryMissing = !loading && Boolean(categorySlug) && !current;
  const visibleGroups = categorySlug ? (current ? [current] : []) : list;
  const facilityCount = list.reduce((total, category) => total + category.items.length, 0);
  const visibleCount = visibleGroups.reduce((total, category) => total + category.items.length, 0);
  const pageTitle = categorySlug ? (current?.label || 'Kategori Fasilitas') : 'Sarana & Prasarana';

  return (
    <div data-testid="sarana-prasarana-page">
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1"><Home size={14} /> Beranda</Link>
              <ChevronRight size={14} />
              {categorySlug ? (
                <>
                  <Link to="/sarana-prasarana" className="hover:text-white transition-colors">Sarana &amp; Prasarana</Link>
                  <ChevronRight size={14} />
                  <span className="text-white">{current?.label || categorySlug}</span>
                </>
              ) : <span className="text-white">Sarana &amp; Prasarana</span>}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-4">{pageTitle}</h1>
            <p className="text-white/80 max-w-xl text-base">
              {current
                ? `Jelajahi ${current.label.toLowerCase()} yang tersedia untuk mendukung kegiatan sivitas akademika STIKES HI Jambi.`
                : 'Fasilitas lengkap dan modern untuk mendukung proses pembelajaran dan pengembangan diri mahasiswa STIKES HI Jambi.'}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020]">
        {/* Stats */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { val: String(facilityCount), label: 'Fasilitas Tersedia', icon: <Building2 size={18} /> },
              { val: String(list.length), label: 'Kategori Fasilitas', icon: <FlaskConical size={18} /> },
              { val: String(visibleCount), label: current ? 'Di Kategori Ini' : 'Sedang Ditampilkan', icon: <BookOpen size={18} /> },
              { val: 'Terkini', label: 'Data dari Kampus', icon: <Wifi size={18} /> },
            ].map((s, i) => (
              <div key={i} className="bg-white dark:bg-[#1A182E] rounded-2xl p-5 text-center border border-gray-100 dark:border-gray-800 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-[#7B2FBE] flex items-center justify-center mx-auto mb-2">{s.icon}</div>
                <div className="text-xl font-bold text-[#1A1A2E] dark:text-white font-heading">{s.val}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 mb-8 flex-wrap">
            <Link
              to="/sarana-prasarana"
              data-testid="category-all"
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                !categorySlug
                  ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/20'
                  : 'bg-white dark:bg-[#1A182E] text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-[#7B2FBE]'
              }`}
            >
              <LayoutGrid size={20} /> Semua Fasilitas
            </Link>
            {list.map(f => (
              <Link
                key={f.id}
                to={`/sarana-prasarana/kategori/${encodeURIComponent(f.id)}`}
                data-testid={`category-${f.id}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  categorySlug === f.id
                    ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/20'
                    : 'bg-white dark:bg-[#1A182E] text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-[#7B2FBE]'
                }`}
              >
                {f.icon} {f.label}
              </Link>
            ))}
          </div>

          {/* Facility Grid */}
          {loading && (
            <div className="flex justify-center py-16">
              <Loader2 className="animate-spin text-[#7B2FBE]" size={28} />
            </div>
          )}
          {categoryMissing && (
            <div className="mb-16 rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center dark:border-gray-700 dark:bg-[#1A182E]">
              <Building2 size={34} className="mx-auto text-gray-300 dark:text-gray-600" />
              <h2 className="mt-4 font-bold text-[#1A1A2E] dark:text-white">Kategori tidak ditemukan</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Slug kategori mungkin telah berubah atau kategori sudah tidak tersedia.</p>
              <Link to="/sarana-prasarana" className="mt-5 inline-flex rounded-xl bg-[#7B2FBE] px-5 py-2.5 text-sm font-semibold text-white">Lihat semua fasilitas</Link>
            </div>
          )}
          {!loading && !categoryMissing && (
            <div className="space-y-12 pb-16">
              {visibleGroups.map((category, groupIndex) => (
                <motion.section
                  key={category.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: groupIndex * 0.06 }}
                >
                  {!categorySlug && (
                    <div className="mb-5 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <span className="grid h-10 w-10 place-items-center rounded-xl bg-purple-100 text-[#7B2FBE] dark:bg-purple-900/30 dark:text-purple-300">{category.icon}</span>
                        <div>
                          <h2 className="font-heading text-xl font-bold text-[#1A1A2E] dark:text-white">{category.label}</h2>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{category.items.length} fasilitas tersedia</p>
                        </div>
                      </div>
                      <Link to={`/sarana-prasarana/kategori/${encodeURIComponent(category.id)}`} className="hidden items-center gap-1.5 text-sm font-semibold text-[#7B2FBE] hover:underline sm:flex dark:text-purple-300">
                        Lihat kategori <ChevronRight size={16} />
                      </Link>
                    </div>
                  )}
                  {category.items.length ? (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {category.items.map((item, itemIndex) => (
                        <motion.div
                          key={item.slug || itemIndex}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: itemIndex * 0.06 }}
                          data-testid={`facility-card-${category.id}-${itemIndex}`}
                        >
                          <FacilityCard item={item} />
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">Belum ada fasilitas pada kategori ini.</div>
                  )}
                </motion.section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaranaPrasarana;
